import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import * as Tooltip from "@radix-ui/react-tooltip";
import { FLARE_EXPLORER_URL } from "../../shared/constants";
import { formatCompactFlr, formatFlr, formatUsdFromWei } from "../../shared/format";
import type { LiveBlock, PriceSample } from "../../shared/types";
import { ConnectionIcon, ExternalLinkIcon, InfoIcon, PauseIcon, PlayIcon } from "./Icons";
import {
  BURNSTREAM_BAR_REVEAL_DELAY_SECONDS,
  burnstreamWindow,
  completeBurnstreamStep,
  initialBurnstreamSlots,
  observedBlockCadenceSeconds,
  queueCorrectedCadenceSeconds,
  roundedScaleMaximum,
  seedBurnstreamPlayback,
  stageBurnstreamBlock,
} from "./burnstreamMath";
import { BURNSTREAM_SCALE_EXPAND_SECONDS, useAnimatedScale, useStickyScale } from "./useStickyScale";

gsap.registerPlugin(useGSAP);

const VIEW_HEIGHT = 360;
const PLOT_TOP = 24;
const PLOT_BOTTOM = 332;
const PLOT_HEIGHT = PLOT_BOTTOM - PLOT_TOP;
const MOBILE_CHART_HEIGHT = 240;

function asFlr(value: string): number {
  return Number(BigInt(value)) / 1e18;
}

function useMobileWindow(): boolean {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 760px)").matches);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px)");
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return mobile;
}

function utcTime(timestamp: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone: "UTC",
  }).format(new Date(timestamp));
}

function severityLabel(block: LiveBlock): string {
  return block.severity === "routine" ? "Routine burn" : block.severity === "elevated" ? "Elevated burn" : "Exceptional burn";
}

interface BurnstreamProps {
  blocks: LiveBlock[];
  price: PriceSample | null;
  connection: "connecting" | "live" | "reconnecting";
  onBlockPresented?: (block:LiveBlock)=>void;
}

export function Burnstream({ blocks, price, connection, onBlockPresented }: BurnstreamProps) {
  const mobile = useMobileWindow();
  const capacity = mobile ? 20 : 50;
  const initialPlayback = seedBurnstreamPlayback(blocks, capacity);
  const observedCadence = observedBlockCadenceSeconds(blocks);
  const [paused, setPaused] = useState(false);
  const [slots, setSlots] = useState<Array<LiveBlock | null>>(() => initialBurnstreamSlots(initialPlayback.visible, capacity));
  const [inspected, setInspected] = useState<LiveBlock | null>(null);
  const [activeBlock, setActiveBlock] = useState<string | null>(null);
  const [inspectionOffset, setInspectionOffset] = useState(0);
  const root = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const links = useRef(new Map<string, HTMLElement>());
  const trackTween = useRef<gsap.core.Animation | null>(null);
  const pumpPlayback = useRef<(() => void) | null>(null);
  const pausedRef = useRef(false);
  const cadenceRef = useRef(observedCadence);
  const pendingBlocks = useRef<LiveBlock[]>([...initialPlayback.pending]);
  const latestBlocks = useRef(blocks);
  const seenBlocks = useRef(new Set(blocks.map((block) => block.blockNumber)));
  const onBlockPresentedRef=useRef(onBlockPresented);
  const chartPixelHeight = mobile ? MOBILE_CHART_HEIGHT : 360;
  const [viewWidth, setViewWidth] = useState(() => {
    const available = typeof window === "undefined" ? 1_000 : Math.max(320, window.innerWidth - (window.innerWidth <= 760 ? 20 : 48));
    return available / chartPixelHeight * VIEW_HEIGHT;
  });
  useEffect(() => {
    if (!stage.current) return;
    const observer = new ResizeObserver(([entry]) => setViewWidth(Math.max(430, entry.contentRect.width / chartPixelHeight * VIEW_HEIGHT)));
    observer.observe(stage.current);
    return () => observer.disconnect();
  }, [chartPixelHeight]);
  useEffect(() => {
    latestBlocks.current = blocks;
    cadenceRef.current = cadenceRef.current * 0.72 + observedBlockCadenceSeconds(blocks) * 0.28;
  }, [blocks]);
  useEffect(()=>{onBlockPresentedRef.current=onBlockPresented;},[onBlockPresented]);
  useEffect(() => {
    const playback = seedBurnstreamPlayback(latestBlocks.current, capacity);
    pendingBlocks.current = [...playback.pending];
    seenBlocks.current = new Set(latestBlocks.current.map((block) => block.blockNumber));
    setSlots(initialBurnstreamSlots(playback.visible, capacity));
  }, [capacity]);
  useEffect(() => {
    const incoming = blocks
      .filter((block) => !seenBlocks.current.has(block.blockNumber))
      .sort((left, right) => BigInt(left.blockNumber) < BigInt(right.blockNumber) ? -1 : 1);
    for (const block of incoming) {
      seenBlocks.current.add(block.blockNumber);
      pendingBlocks.current.push(block);
    }
    if (pendingBlocks.current.length > 100) pendingBlocks.current = pendingBlocks.current.slice(-100);
    if (!incoming.length) return;
    const frame = window.requestAnimationFrame(() => pumpPlayback.current?.());
    return () => window.cancelAnimationFrame(frame);
  }, [blocks]);
  const visibleBlocks = slots.slice(0, capacity).filter((block): block is LiveBlock => block !== null);
  const scaleBlocks = slots.filter((block): block is LiveBlock => block !== null);
  const tableBlocks = burnstreamWindow(blocks, capacity);
  // Include the staged off-screen block so a larger range starts easing into
  // place before its bar crosses the plot edge.
  const scaleTarget = useStickyScale(scaleBlocks.map((block) => asFlr(block.totalBurnWei)));
  const animatedScaleMaximum = useAnimatedScale(scaleTarget);
  const visibleScaleFloor = roundedScaleMaximum(Math.max(0, ...visibleBlocks.map((block) => asFlr(block.totalBurnWei))));
  // This is the chart's safety invariant: animation may influence the range,
  // but it can never make the axis smaller than the tallest visible datum.
  const scaleMaximum = Math.max(animatedScaleMaximum, visibleScaleFloor);
  const plot = { left: mobile ? 56 : 72, right: viewWidth - 24, top: PLOT_TOP, bottom: PLOT_BOTTOM };
  const slotWidth = (plot.right - plot.left) / capacity;
  const barWidth = Math.max(6, slotWidth * 0.56);

  useGSAP((_context, contextSafe) => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const track = root.current?.querySelector<SVGGElement>("[data-burn-track]");
    if (!track) return;
    gsap.set(track, { x: 0 });
    let disposed = false;
    let pump = () => {};
    const finishStepRaw = () => {
      if (disposed) return;
      // Reset and recycle in one JS task so there is no painted seam between
      // the translated strip and its new logical slot positions.
      gsap.set(track, { x: 0 });
      flushSync(() => setSlots((current) => completeBurnstreamStep(current)));
      trackTween.current = null;
      pump();
    };
    const finishStep = contextSafe?.(finishStepRaw) ?? finishStepRaw;
    const pumpRaw = () => {
      if (disposed || pausedRef.current || trackTween.current || !pendingBlocks.current.length) return;
      const queueDepth = pendingBlocks.current.length;
      const next = pendingBlocks.current.shift();
      if (!next) return;
      if (reduceMotion) {
        flushSync(() => setSlots((current) => completeBurnstreamStep(stageBurnstreamBlock(current, next, capacity))));
        onBlockPresentedRef.current?.(next);
        window.requestAnimationFrame(pump);
        return;
      }
      // Stage the confirmed block one exact slot beyond the right edge. It is
      // visible only as the single compositor-driven track glides left.
      flushSync(() => setSlots((current) => stageBurnstreamBlock(current, next, capacity)));
      const entry = track.querySelector<SVGGElement>(`[data-burn-group="${next.blockNumber}"]`);
      const entryBar = entry?.querySelector<SVGRectElement>("[data-burn-rect]");
      const entryMarker = entry?.querySelector<SVGCircleElement>(".burnstream__zero-marker");
      const playbackDuration = queueCorrectedCadenceSeconds(cadenceRef.current, queueDepth);
      const edgeCrossing = playbackDuration * Math.max(0.16, (slotWidth - barWidth) / (2 * slotWidth));
      // Never reveal a rising bar until an expanding Y-axis tween has had
      // enough time to reach its target. The normal cadence already provides
      // more lead-in; the explicit floor keeps that relationship future-safe.
      const revealAt = Math.max(
        edgeCrossing + BURNSTREAM_BAR_REVEAL_DELAY_SECONDS,
        BURNSTREAM_SCALE_EXPAND_SECONDS + 0.08,
      );
      const revealDuration = Math.min(0.72, Math.max(0.32, playbackDuration - revealAt));
      const timeline = gsap.timeline({ onComplete: finishStep });
      // This is the single live presentation event shared by the new bar and
      // the burn funnel. Keeping it on the reveal beat prevents either view
      // from creating its own clock or drifting onto a different block.
      timeline.call(() => onBlockPresentedRef.current?.(next), [], revealAt);
      timeline.to(track, {
        x: -slotWidth,
        duration: playbackDuration,
        ease: "none",
        force3D: true,
        overwrite: true,
      }, 0);
      if (entryBar) {
        // React owns the numeric y/height attributes because they respond to
        // the animated Y-axis maximum. Reveal with a compositor transform so
        // GSAP never restores geometry captured against an obsolete scale.
        timeline.fromTo(entryBar, {
          scaleY: 0,
          opacity: 0.45,
          transformOrigin: "50% 100%",
        }, {
          scaleY: 1,
          opacity: 1,
          duration: revealDuration,
          ease: "power3.out",
          clearProps: "transform,transformOrigin",
        }, revealAt);
      } else if (entryMarker) {
        timeline.fromTo(entryMarker, {
          scale: 0,
          opacity: 0,
          transformOrigin: "50% 50%",
        }, {
          scale: 1,
          opacity: 1,
          duration: Math.min(0.42, revealDuration),
          ease: "back.out(2)",
        }, revealAt);
      }
      trackTween.current = timeline;
    };
    pump = contextSafe?.(pumpRaw) ?? pumpRaw;
    pumpPlayback.current = pump;
    const startFrame = window.requestAnimationFrame(pump);
    return () => {
      disposed = true;
      window.cancelAnimationFrame(startFrame);
      trackTween.current?.kill();
      trackTween.current = null;
      if (pumpPlayback.current === pump) pumpPlayback.current = null;
    };
  }, { scope: root, dependencies: [barWidth, capacity, slotWidth], revertOnUpdate: true });

  const togglePause = () => {
    const next = !paused;
    pausedRef.current = next;
    setPaused(next);
    if (next) trackTween.current?.pause();
    else if (trackTween.current) trackTween.current.resume();
    else window.requestAnimationFrame(() => pumpPlayback.current?.());
  };

  const inspect = (block: LiveBlock) => {
    const track = root.current?.querySelector<SVGGElement>("[data-burn-track]");
    setInspectionOffset(track ? Number(gsap.getProperty(track, "x")) || 0 : 0);
    setActiveBlock(block.blockNumber);
    setInspected(block);
  };

  const releaseInspection = () => {
    setInspected(null);
  };

  const moveFocus = (current: string, key: string) => {
    const index = visibleBlocks.findIndex((block) => block.blockNumber === current);
    const targetIndex = key === "Home" ? 0 : key === "End" ? visibleBlocks.length - 1 : key === "ArrowLeft" ? Math.max(0, index - 1) : Math.min(visibleBlocks.length - 1, index + 1);
    const target = visibleBlocks[targetIndex];
    if (!target) return;
    inspect(target);
    links.current.get(target.blockNumber)?.focus();
  };

  const inspectedIndex = inspected ? slots.findIndex((block) => block?.blockNumber === inspected.blockNumber) : -1;
  const effectiveActiveBlock = activeBlock && visibleBlocks.some((block) => block.blockNumber === activeBlock)
    ? activeBlock
    : visibleBlocks.at(-1)?.blockNumber ?? null;
  const inspectedX = inspectedIndex >= 0 ? plot.left + (inspectedIndex + 0.5) * slotWidth + inspectionOffset : 0;
  const yTicks = [1, 0.75, 0.5, 0.25, 0];
  const emptyHeadline = connection === "connecting"
    ? "Connecting to the live block stream"
    : connection === "reconnecting"
      ? "Reconnecting to the live block stream"
      : "Confirming the first live blocks";

  return (
    <section ref={root} id="burnstream" className={`burnstream burnstream--${connection}${paused ? " burnstream--paused" : ""}`} aria-labelledby="burnstream-title">
      <div className="burnstream__header">
        <div>
          <h2 id="burnstream-title">The Burnstream</h2>
        </div>
        <div className="burnstream__controls">
          <span className={`burnstream__connection burnstream__connection--${connection}`}><ConnectionIcon />{connection === "live" ? "Live feed" : connection}</span>
          <Tooltip.Root>
            <Tooltip.Trigger className="icon-button burnstream__help" aria-label="How to read The Burnstream"><InfoIcon /></Tooltip.Trigger>
            <Tooltip.Portal><Tooltip.Content className="tooltip" sideOffset={8}>Each bar is one block, played from a short confirmed buffer at Flare's average block cadence. Height is total FLR burned; highlighted bars crossed the rolling server baseline.<Tooltip.Arrow className="tooltip__arrow" /></Tooltip.Content></Tooltip.Portal>
          </Tooltip.Root>
          <button className="burnstream__pause" type="button" onClick={togglePause} aria-pressed={paused}>
            {paused ? <PlayIcon /> : <PauseIcon />}<span>{paused ? "Resume view" : "Pause view"}</span>
          </button>
        </div>
      </div>

      {paused && <div className="burnstream__status"><span>View frozen at block {visibleBlocks.at(-1)?.blockNumber ?? "—"}; collection continues.</span></div>}

      <div ref={stage} className="burnstream__stage">
        <svg data-scale-maximum={scaleMaximum} viewBox={`0 0 ${viewWidth} ${VIEW_HEIGHT}`} role="group" aria-label={`Live burn chart with ${visibleBlocks.length} evenly spaced confirmed blocks`}>
          <defs>
            <clipPath id="burnstream-plot-clip"><rect x={plot.left} y={0} width={plot.right - plot.left} height={VIEW_HEIGHT} /></clipPath>
          </defs>

          {yTicks.map((tick) => {
            const y = plot.top + (1 - tick) * PLOT_HEIGHT;
            return <g key={tick} className="burnstream__gridline"><line x1={plot.left} x2={plot.right} y1={y} y2={y}/><text x={plot.left - 12} y={y + 4}>{formatCompactFlr(BigInt(Math.round(scaleMaximum * tick * 1e6)) * 10n ** 12n)}</text></g>;
          })}
          <text className="burnstream__axis-title" x={14} y={plot.top + PLOT_HEIGHT / 2} transform={`rotate(-90 14 ${plot.top + PLOT_HEIGHT / 2})`}>FLR BURNED</text>

          {!visibleBlocks.length && (
            <g className="burnstream__empty"><line x1={plot.left} x2={plot.right} y1={plot.bottom} y2={plot.bottom}/><text x={(plot.left + plot.right) / 2} y={plot.top + PLOT_HEIGHT / 2 - 5}>{emptyHeadline}</text><text className="burnstream__empty-sub" x={(plot.left + plot.right) / 2} y={plot.top + PLOT_HEIGHT / 2 + 20}>This session begins empty; blocks appear after database confirmation.</text></g>
          )}

          <g clipPath="url(#burnstream-plot-clip)">
            <g data-burn-track className="burnstream__track">
            {slots.map((block, index) => {
              if (!block) return null;
              const zero = BigInt(block.totalBurnWei) === 0n;
              const height = Math.max(0, Math.min(PLOT_HEIGHT, asFlr(block.totalBurnWei) / scaleMaximum * PLOT_HEIGHT));
              const href = `${FLARE_EXPLORER_URL}/block/${block.blockNumber}`;
              return (
                <g key={block.blockNumber} data-burn-group={block.blockNumber} transform={`translate(${plot.left + index * slotWidth + (slotWidth - barWidth) / 2} 0)`} className={`burnstream__bar-group burnstream__bar-group--${block.severity}`}>
                  <a
                    ref={(node) => { if (node) links.current.set(block.blockNumber, node); else links.current.delete(block.blockNumber); }}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    tabIndex={effectiveActiveBlock === block.blockNumber ? 0 : -1}
                    aria-label={`Block ${block.blockNumber}, ${formatFlr(block.totalBurnWei)} FLR burned, ${severityLabel(block)}. Open in Flare Explorer (new tab).`}
                    onFocus={() => inspect(block)}
                    onBlur={releaseInspection}
                    onMouseEnter={() => inspect(block)}
                    onMouseLeave={() => { if (document.activeElement !== links.current.get(block.blockNumber)) releaseInspection(); }}
                    onKeyDown={(event) => {
                      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
                        event.preventDefault();
                        moveFocus(block.blockNumber, event.key);
                      }
                    }}
                  >
                    {!zero && <rect data-burn-rect className="burnstream__bar" x={0} y={plot.bottom - height} width={barWidth} height={height} rx={Math.min(3, barWidth / 4)} />}
                    {zero && <circle className="burnstream__zero-marker" cx={barWidth / 2} cy={plot.bottom - 1} r={Math.max(4, barWidth * 0.25)} />}
                  </a>
                </g>
              );
            })}
            </g>
          </g>

          {inspected && inspectedIndex >= 0 && <g className="burnstream__crosshair" aria-hidden="true"><line x1={inspectedX} x2={inspectedX} y1={plot.top} y2={plot.bottom}/><line x1={plot.left} x2={plot.right} y1={plot.bottom - Math.min(PLOT_HEIGHT, asFlr(inspected.totalBurnWei) / scaleMaximum * PLOT_HEIGHT)} y2={plot.bottom - Math.min(PLOT_HEIGHT, asFlr(inspected.totalBurnWei) / scaleMaximum * PLOT_HEIGHT)}/></g>}
        </svg>

        {inspected && (
          <div className={`burnstream__tooltip burnstream__tooltip--${inspected.severity}`} style={{ left: `${Math.max(17, Math.min(83, inspectedX / viewWidth * 100))}%` }} role="tooltip">
            <div className="burnstream__tooltip-title"><span>Block {new Intl.NumberFormat("en-GB").format(Number(inspected.blockNumber))}</span><span>{severityLabel(inspected)}</span></div>
            <strong>{formatFlr(inspected.totalBurnWei)} FLR</strong>
            <dl>
              <div><dt>Burned base fees</dt><dd>{formatFlr(inspected.feeBurnWei)} FLR</dd></div>
              <div><dt>Canonical burn</dt><dd>{formatFlr(inspected.canonicalBurnWei)} FLR</dd></div>
              <div><dt>Current-price estimate</dt><dd>{price && !price.stale ? formatUsdFromWei(inspected.totalBurnWei, price.valueE18) : "Unavailable · price stale"}</dd></div>
              <div><dt>Block time</dt><dd>{utcTime(inspected.timestamp)} UTC</dd></div>
            </dl>
            <span className="burnstream__tooltip-link">Open block <ExternalLinkIcon /></span>
          </div>
        )}
      </div>

      <details className="data-table-disclosure burnstream__table">
        <summary>View recent blocks as a table</summary>
        <div className="data-table-wrap">
          <table>
            <thead><tr><th scope="col">Block</th><th scope="col">Time (UTC)</th><th scope="col">Fee burn (FLR)</th><th scope="col">Canonical burn (FLR)</th><th scope="col">Total (FLR)</th><th scope="col">Severity</th><th scope="col">Explorer</th></tr></thead>
            <tbody>{tableBlocks.map((block) => <tr key={block.blockNumber}><th scope="row">{block.blockNumber}</th><td>{utcTime(block.timestamp)}</td><td>{formatFlr(block.feeBurnWei)}</td><td>{formatFlr(block.canonicalBurnWei)}</td><td>{formatFlr(block.totalBurnWei)}</td><td>{severityLabel(block)}</td><td><a href={`${FLARE_EXPLORER_URL}/block/${block.blockNumber}`} target="_blank" rel="noreferrer" aria-label={`Open block ${block.blockNumber} in Flare Explorer (new tab)`}>Open <ExternalLinkIcon /></a></td></tr>)}</tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
